/* eslint-disable prefer-destructuring,no-underscore-dangle */
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { generateQueryParams } from '../helper';
import router from '../router';

export const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 120000,
  headers: { common: {} },
});

export const axiosUnauthorizedInstance = axios.create({
  timeout: 120000,
  headers: { common: {} },
});

export function storeApiPlugin(store) {
  // Function that will be called to refresh authorization
  const refreshAuthLogic = (failedRequest) =>
    axiosInstance
      .post('auth/token/refresh/', { refresh: store.state.user.tokens.refresh })
      .then((tokenRefreshResponse) => {
        store.commit('LOGIN', {
          access: tokenRefreshResponse.data.access,
          refresh: store.state.user.tokens.refresh,
        });
        failedRequest.response.config.headers['Authorization'] =
          'Bearer ' + tokenRefreshResponse.data.token;
        return Promise.resolve(failedRequest);
      });

  const appendAuthHeader = (request) => {
    if (store.state.user.tokens.access && !request.url.includes('.s3.')) {
      request.headers.Authorization = `Bearer ${store.state.user.tokens.access}`;
    }
    return Promise.resolve(request);
  };

  const logoutIfRefreshTokenExpired = (error) => {
    // If we get 401 while refreshing with refresh token,
    // log out and do not run refresh logic again
    if (
      !store.state.user.tokens.refresh ||
      (error.response.status === 401 && error.config.url.endsWith('auth/token/refresh/'))
    ) {
      store.dispatch('userSignout');
      if (router.currentRoute.path !== '/login') {
        router.push('/login');
      }
      return false;
    }
    return true;
  };

  // Create the auth refresh interceptor
  createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic, {
    shouldRefresh: (error) => logoutIfRefreshTokenExpired(error),
  });

  // Setup headers on load of plugin and page, to make request on refresh works
  axiosInstance.interceptors.request.use(
    (config) => appendAuthHeader(config, store.state.user.tokens.access),
    (error) => Promise.reject(error)
  );
}

/**
 * Fetch token for authenticated user
 * @returns { Promise }
 */
export const fetchToken = (user) => axiosInstance.post('auth/token/', user);

/**
 * Fetch token for authenticated Azure user
 * @returns { Promise }
 */
export const fetchAzureToken = (user) => axiosInstance.post('auth/social-typless/', user);

// /**
//  * Refresh token when it expires, to successfully execute new request
//  * @returns { Promise }
//  */
// export const refreshToken = () => axiosInstance.post('auth/token/refresh/');

/**
 * Get all unprocessed invoices.
 *
 * @return {Promise}
 */
export const getUnprocessedInvoices = (url) => axiosInstance.get(url);

/**
 * Change review status of an invoice.
 *
 * @param aggregate_id: id of given document to update the status
 * @param review_status: Review status to set to the given document
 * @param username: Username of the user that changed the status
 * @return {Promise}
 */

export const changeReviewStatus = (aggregate_id, review_status, username, rejection) =>
  axiosInstance.post('v1/command/change-review-status', {
    aggregate_id,
    review_status,
    username,
    rejection,
  });

/**
 * Get invoice data for given id
 *
 * @param id Id of the given invoice
 *
 * */

export const getInvoiceData = (url) => axiosInstance.get(url);

/**
 * Get invoice data for review
 *
 * @param id Id of the given invoice
 *
 * */

export const getInvoiceForReview = () =>
  axiosInstance.get('/received-invoices/get-invoice-for-review/');

/**
 * Download file to return arraybuffer of file
 * @param url Url path to file
 * @returns { Promise }
 */
export const downloadFile = (url) =>
  axiosUnauthorizedInstance.get(url, { responseType: 'arraybuffer' });

/**
 * Set invoice data for give ID to DB
 *
 * @param aggregate_id Id of the given Invoice
 * @param review_status Review status of the set invoice
 * @param data Invoice data with which to update
 * @param merged_document_keys Keys of documents to merge images
 *
 * */

export const setInvoiceData = (url, data) =>
  axiosInstance
    // eslint-disable-next-line no-underscore-dangle
    .patch(url, data, {
      headers: { 'Content-Type': `multipart/form-data; boundary=${data._boundary}` },
    });

/**
 * Get enterprise config
 *
 *
 * */

export const getEnterpriseConfig = () => axiosInstance.get('/standard-settings/');

/**
 *
 *
 * @param data: Data to update enterprise config with
 * @return { Promise } of the request
 * * */

export const updateEnterpriseConfig = (data) => axiosInstance.post('/standard-settings/', data);

/**
 * Get processed invoices for given queryParams
 * @param queryParams query string params
 * @return { Promise } of the request
 * */
export const fetchProcessedInvoices = (queryParams) => {
  return axiosInstance.get(`received-invoices/?${queryParams}`);
};

/**
 * Export processed invoices as CSV with given filter queryParams
 * @param queryParams query string params (same as list endpoint)
 * @return { Promise } resolving to a Blob response
 * */
export const exportInvoicesCsv = (queryParams) => {
  return axiosInstance.get(`received-invoices/export-csv/?${queryParams}`, {
    responseType: 'blob',
  });
};

/**
 * Search partners for autocomplete dropdown
 * @param query search text (name or vat_id)
 * @return { Promise } of the request
 * */
export const searchPartnersForFilter = (query) => {
  return axiosInstance.get(`/partner/?query=${encodeURIComponent(query)}`);
};

/**
 * Get processed by user
 * @param params: Parameters to convert into query string params
 * @return { Promise } of the request
 * */
export const fetchProcessedByUser = (params) => {
  const queryParams = generateQueryParams(params);
  return axiosInstance.get(`/invoices-count/?${queryParams}`);
};

/**
 * Change status of the invoice (Not just a patch on the model, so we can control permissions)
 * @param url Invoice resource url
 * @param newStatus Status to patch with (WR, RU..)
 * @return { Promise } of the request
 * */
export const editInvoiceStatus = (url, newStatus) => {
  return axiosInstance.patch(`${url}change-status/`, { status: newStatus });
};

/**
 * Set external rejection flag on the received invoice.
 * @param id Invoice id
 * @return { Promise } of the request
 * */
export const setExternalRejection = (id) => {
  return axiosInstance.post(`received-invoices/set-external-rejection/`, { external_id: id });
};

/**
 * Get list for Partners
 * @param params Parameters to convert into query string params
 * @return { Promise } of the request
 * */
export const fetchPartners = (params) => {
  const queryParams = generateQueryParams(params);
  return axiosInstance.get(`/partner/?${queryParams}`);
};

/**
 * Create new Partners
 * @param vat_id Vat ID
 * @param to_mail Email
 * @return { Promise } of the request
 * */
export const createNewPartner = (payload) => axiosInstance.post('/partner/', payload);

/**
 * Edit Partners
 * @param payload payload to update with
 * @return { Promise } of the request
 * */
export const editPartner = (payload) => axiosInstance.patch(payload.url, payload);

/**
 * Delete Partners
 * @param payload payload to update with
 * @return { Promise } of the request
 * */
export const deletePartner = ({ url }) => axiosInstance.delete(url);

/**
 * Check if database connection works
 * @param payload: partner integration and provided setings from UI
 * @returns { Promise }
 */
export const testPartnerConnectivity = (payload) =>
  axiosInstance.post(`partner/test-partner-connectivity/`, payload);

/**
 * Get whitelist for Partner by vat_id
 * @param id Whitelist ID
 * @return { Promise } of the request
 * */
export const getWhitelist = (id) => axiosInstance.get(`/whitelist/${id}/`);

/**
 * Create new Whitelist for Partner
 * @param payload Whitelist data
 * @return { Promise } of the request
 * */
export const createWhitelist = (payload) => axiosInstance.post('/whitelist/', payload);

/**
 * Update Whitelist for Partner
 * @param id Whitelist ID
 * @param payload Updated whitelist data
 * @return { Promise } of the request
 * */
export const updateWhitelist = (id, payload) => axiosInstance.put(`/whitelist/${id}/`, payload);

/**
 * Get list of email mappings for a customer
 * @return { Promise } of the request
 * */
export const fetchEmailMappings = (params) => {
  const queryParams = generateQueryParams(params);
  return axiosInstance.get(`/customer-email-mapping/?${queryParams}`);
};

/**
 * Create new email mapping
 * @param email the customer email
 * @param emails_to_bounce String of redirection emails, seperated by commas.
 * @return { Promise } of the request
 * */
export const createEmailMapping = (payload) =>
  axiosInstance.post('/customer-email-mapping/', payload);

/**
 * Edit email mapping
 * @param payload payload to update with
 * @return { Promise } of the request
 * */
export const editEmailMapping = (payload) =>
  axiosInstance.put(`/customer-email-mapping/${payload.id}/`, payload);

/**
 * Delete email mapping
 * @param id id of the mapping
 * @return { Promise } of the request
 * */
export const deleteEmailMapping = (id) => axiosInstance.delete(`/customer-email-mapping/${id}/`);

/**
 * Get list of existing payment types
 * @return { Promise } of the request
 * */
export const fetchPaymentTypes = () => {
  return axiosInstance.get(`/payment-type/`);
};

/**
 * Create new payment type
 * @param payload the PaymentType object
 * @return { Promise } of the request
 * */
export const createPaymentType = (payload) => axiosInstance.post('/payment-type/', payload);

/**
 * Edit payment type
 * @param payload PaymentType object to update with
 * @return { Promise } of the request
 * */
export const editPaymentType = (payload) =>
  axiosInstance.put(`/payment-type/${payload.id}/`, payload);

/**
 * Delete payment type
 * @param id id of the mapping
 * @return { Promise } of the request
 * */
export const deletePaymentType = (id) => axiosInstance.delete(`/payment-type/${id}/`);

/**
 * Get list of all possible currencies
 * @return { Promise } of the request
 * */
export const fetchAllCurrencies = () => axiosInstance.get(`/all-currencies/`);

/**
 * Get list of currencies
 * @return { Promise } of the request
 * */
export const fetchCurrencies = () => axiosInstance.get(`/currencies/`);

/**
 * Create new currency
 * @param iso_code the 3 character currency ISO code
 * @return { Promise } of the request
 * */
export const createCurrency = (payload) => axiosInstance.post('/currencies/', payload);

/**
 * Delete a currency
 * @param id id of the currency
 * @return { Promise } of the request
 * */
export const deleteCurrency = (id) => axiosInstance.delete(`/currencies/${id}/`);

/**
 * Get list of partner iban mappings
 * @return { Promise } of the request
 * */
export const fetchPartnerIbans = () => axiosInstance.get(`/partner-iban/`);

/**
 * Run a job on backend to sync list of partner iban mappings with PA table. Get updated partner ibans
 * @return { Promise } of the request
 * */
export const syncPartnerIbans = () => axiosInstance.post(`/partner-iban/sync/`);

/**
 * Get number of unreviewed invoices
 * @return { Promise } of the request
 * */
export const fetchUnreviewedCount = () => axiosInstance.get('v1/query/unreviewed-count');

/**
 * Get all unsplit attachments, waiting for it to be split by user
 * @returns { Promise }
 */
export const getUnsplitAttachments = (url) => axiosInstance.get(url);

/**
 * Get specific attachment
 * @returns { Promise }
 */
export const getAttachmentByUrl = (url) => axiosInstance.get(url);

/**
 * Get list of existing countries
 * @return { Promise } of the request
 * */
export const fetchCountries = () => {
  return axiosInstance.get(`/countries/`);
};

/**
 * Create new country
 * @param payload the Country object
 * @return { Promise } of the request
 * */
export const createCountry = (payload) => axiosInstance.post('/countries/', payload);

/**
 * Edit country
 * @param payload Country object to update with
 * @return { Promise } of the request
 * */
export const editCountry = (payload) => axiosInstance.put(`/countries/${payload.id}/`, payload);

/**
 * Delete country
 * @param id id of the mapping
 * @return { Promise } of the request
 * */
export const deleteCountry = (id) => axiosInstance.delete(`/countries/${id}/`);

/**
 * Get external approval invoice details using token
 * @param {string} token - The approval token
 * @returns {Promise} Promise of the request
 */
export const getInvoiceForExternalApproval = (token) => {
  return axiosUnauthorizedInstance.get(
    `${process.env.VUE_APP_API_URL}/external-approval/?token=${token}`
  );
};

/**
 * Submit external approval decision
 * @param {Object} payload - Contains token and approval decision
 * @param {string} payload.token - The approval token
 * @param {boolean} payload.approved - Whether the invoice is approved or rejected
 * @returns {Promise} Promise of the request
 */
export const submitExternalApproval = (payload) => {
  return axiosUnauthorizedInstance.post(
    `${process.env.VUE_APP_API_URL}/external-approval/`,
    payload
  );
};

/**
 * Get list of document issuers
 * @param params Parameters to convert into query string params
 * @return { Promise } of the request
 * */
export const fetchDocumentIssuers = (params) => {
  const queryParams = generateQueryParams(params);
  return axiosInstance.get(`/document-issuers/?${queryParams}`);
};

/**
 * Create new document issuer
 * @param payload The document issuer data
 * @return { Promise } of the request
 * */
export const createDocumentIssuer = (payload) => axiosInstance.post('/document-issuers/', payload);

/**
 * Update document issuer
 * @param payload Document issuer object to update
 * @return { Promise } of the request
 * */
export const updateDocumentIssuer = (payload) =>
  axiosInstance.put(`/document-issuers/${payload.id}/`, payload);

/**
 * Delete document issuer
 * @param id id of the document issuer
 * @return { Promise } of the request
 * */
export const deleteDocumentIssuer = (id) => axiosInstance.delete(`/document-issuers/${id}/`);
